import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminOperations.css";
import {
  descargarCertificado,
  eliminarCertificado,
  routeGetGetAllCertificate,
} from "./constants";

const CERTIFICADOS_POR_PAGINA = 10;

export const WatchCertificate = () => {
  const [certificados, setCertificados] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  const [busqueda, setBusqueda] = useState("");
  const [cargando, setCargando] = useState(true);
  const [accionCertificado, setAccionCertificado] = useState(null);
  const [resultado, setResultado] = useState(null);
  const navigate = useNavigate();

  const certificadosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) {
      return certificados;
    }

    return certificados.filter((certificado) =>
      [certificado.matricula_alumno, certificado.nombre_archivo].some((valor) =>
        String(valor || "")
          .toLowerCase()
          .includes(termino),
      ),
    );
  }, [busqueda, certificados]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(certificadosFiltrados.length / CERTIFICADOS_POR_PAGINA),
  );
  const indiceInicial = (paginaActual - 1) * CERTIFICADOS_POR_PAGINA;
  const certificadosPagina = certificadosFiltrados.slice(
    indiceInicial,
    indiceInicial + CERTIFICADOS_POR_PAGINA,
  );

  const cargarCertificados = useCallback(async () => {
    setCargando(true);
    setResultado(null);

    try {
      const response = await axios.get(routeGetGetAllCertificate);
      const lista = response.data?.data ?? response.data ?? [];
      setCertificados(Array.isArray(lista) ? lista : []);
    } catch (error) {
      console.error("Error al obtener las constancias:", error);
      setCertificados([]);
      setResultado({
        tipo: "error",
        mensaje: "No fue posible obtener las constancias.",
      });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarCertificados();
  }, [cargarCertificados]);

  useEffect(() => {
    setPaginaActual((pagina) => Math.min(pagina, totalPaginas));
  }, [totalPaginas]);

  const handleDescargarCertificado = async (idArchivo, nombreArchivo) => {
    setAccionCertificado(idArchivo);
    setResultado(null);

    try {
      const response = await axios.get(descargarCertificado, {
        params: { id_archivo: idArchivo },
        responseType: "blob",
      });
      const fileUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      const nuevaVentana = window.open(fileUrl, "_blank");

      if (nuevaVentana) {
        nuevaVentana.opener = null;
      } else {
        const enlace = document.createElement("a");
        enlace.href = fileUrl;
        enlace.download = nombreArchivo || "constancia.pdf";
        document.body.appendChild(enlace);
        enlace.click();
        enlace.remove();
      }

      window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
    } catch (error) {
      console.error("No se pudo abrir la constancia:", error);
      setResultado({
        tipo: "error",
        mensaje: "No fue posible abrir la constancia.",
      });
    } finally {
      setAccionCertificado(null);
    }
  };

  const handleEliminarCertificado = async (idArchivo, matriculaAlumno) => {
    const confirmado = window.confirm(
      `¿Deseas eliminar la constancia de ${matriculaAlumno}?`,
    );
    if (!confirmado) {
      return;
    }

    setAccionCertificado(idArchivo);
    setResultado(null);

    try {
      const response = await axios.delete(eliminarCertificado, {
        params: { id_archivo: idArchivo },
      });
      setCertificados((listaActual) =>
        listaActual.filter(
          (certificado) => certificado.id_archivo !== idArchivo,
        ),
      );
      setResultado({
        tipo: "success",
        mensaje:
          response.data?.message || "La constancia se eliminó correctamente.",
      });
    } catch (error) {
      console.error("Error al eliminar la constancia:", error);
      setResultado({
        tipo: "error",
        mensaje:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No fue posible eliminar la constancia.",
      });
    } finally {
      setAccionCertificado(null);
    }
  };

  return (
    <div className="admin-operation-page">
      <main className="admin-operation-container">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Documentación académica</p>
          <h1>Constancias</h1>
          <p>
            Busca las constancias cargadas, abre el PDF correspondiente o
            elimina un archivo que ya no deba estar disponible.
          </p>
        </header>

        <section className="admin-operation-panel">
          <div className="admin-operation-toolbar">
            <input
              className="admin-operation-search"
              type="search"
              placeholder="Buscar por matrícula o archivo"
              value={busqueda}
              onChange={(event) => {
                setBusqueda(event.target.value);
                setPaginaActual(1);
              }}
              aria-label="Buscar constancia"
            />
            <div className="admin-operation-toolbar-actions">
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={cargarCertificados}
                disabled={cargando || accionCertificado !== null}
              >
                Actualizar listado
              </button>
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={() => navigate("/admin-home")}
                disabled={accionCertificado !== null}
              >
                Regresar al inicio
              </button>
            </div>
          </div>

          <p className="admin-operation-count">
            {cargando
              ? "Consultando constancias..."
              : `${certificadosFiltrados.length} resultado${certificadosFiltrados.length === 1 ? "" : "s"}`}
          </p>

          {resultado && (
            <div
              className={`admin-operation-status admin-operation-status-${resultado.tipo}`}
              role="status"
            >
              {resultado.mensaje}
            </div>
          )}

          {!cargando && certificadosPagina.length === 0 ? (
            <p className="admin-operation-empty">
              No se encontraron constancias.
            </p>
          ) : (
            <div className="admin-operation-table-wrapper">
              <table className="admin-operation-table">
                <thead>
                  <tr>
                    <th>Matrícula</th>
                    <th>Archivo</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {certificadosPagina.map((certificado) => (
                    <tr key={certificado.id_archivo}>
                      <td>
                        <span className="admin-operation-badge">
                          {certificado.matricula_alumno}
                        </span>
                      </td>
                      <td>{certificado.nombre_archivo || "Constancia PDF"}</td>
                      <td>
                        <div className="admin-operation-table-actions">
                          <button
                            className="admin-operation-button admin-operation-button-compact"
                            type="button"
                            onClick={() =>
                              handleDescargarCertificado(
                                certificado.id_archivo,
                                certificado.nombre_archivo,
                              )
                            }
                            disabled={accionCertificado !== null}
                          >
                            {accionCertificado === certificado.id_archivo
                              ? "Procesando..."
                              : "Ver constancia"}
                          </button>
                          <button
                            className="admin-operation-button admin-operation-button-danger admin-operation-button-compact"
                            type="button"
                            onClick={() =>
                              handleEliminarCertificado(
                                certificado.id_archivo,
                                certificado.matricula_alumno,
                              )
                            }
                            disabled={accionCertificado !== null}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {certificadosFiltrados.length > 0 && (
            <div className="admin-operation-pagination">
              <button
                className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                type="button"
                disabled={paginaActual === 1}
                onClick={() => setPaginaActual((pagina) => pagina - 1)}
              >
                Anterior
              </button>
              <span>
                Página {paginaActual} de {totalPaginas}
              </span>
              <button
                className="admin-operation-button admin-operation-button-secondary admin-operation-button-compact"
                type="button"
                disabled={paginaActual === totalPaginas}
                onClick={() => setPaginaActual((pagina) => pagina + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
